import { logResults } from '@lune/common/logger'
import { Alert, Backdrop, Box, Chip } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState
} from 'react'
import { useOutletContext } from 'react-router-dom'
import { DEFAULT_CONFIG, api as apiConfig } from '../api/config'
import { api as apiGroup } from '../api/group'
import { type Config } from '../api/types'
import { Group } from './group'

const WATCH_INTERVAL_MS = 2000
const MAX_WATCH_ROUNDS = 20

export const Home: React.FC = () => {
  const [loadedTick, setLoadedTick] = useState<number>(-1)
  const [groups, setGroups] = useState<string[]>([])
  const { search: debouncedSearch, clockTick } = useOutletContext<{
    search: string
    clockTick: number
  }>()
  const [search, setSearch] = useState<string>('')
  const [config, setConfig] = useState<Config>()
  const [watchForGroup, setWatchForGroup] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('reload') ?? ''
  })
  const watchRounds = useRef(0)

  const ready = loadedTick === clockTick

  // Read keystrokes directly from the search input to avoid the lib's 1s debounce
  useEffect(() => {
    const onInput = (e: Event): void => {
      const target = e.target as HTMLElement
      if (target.id === 'search') {
        setSearch((target as HTMLInputElement).value)
      }
    }
    document.addEventListener('input', onInput)
    return () => document.removeEventListener('input', onInput)
  }, [])

  const fetchGroups = (): Promise<string[]> =>
    apiGroup.fetch
      .list()
      .then(logResults('groups/list'))
      .then((result) => {
        setGroups(result)
        return result
      })

  useEffect(() => {
    void apiConfig.fetch
      .single(DEFAULT_CONFIG)
      .then(logResults('config/list'))
      .then(setConfig)

    void fetchGroups().then(() => {
      setLoadedTick(clockTick)
    })
  }, [clockTick])

  // Poll the group list until the newly created group appears
  useEffect(() => {
    if (watchForGroup === '') return
    let timer: ReturnType<typeof setTimeout>
    if (groups.includes(watchForGroup)) {
      console.debug(`New group ${watchForGroup} detected. Done tracking!`)
      watchRounds.current = 0
      timer = setTimeout(() => setWatchForGroup(''), 0)
    } else if (watchRounds.current >= MAX_WATCH_ROUNDS) {
      console.debug(`Max watch rounds reached for new group ${watchForGroup}`)
      watchRounds.current = 0
      timer = setTimeout(() => setWatchForGroup(''), 0)
    } else {
      timer = setTimeout(() => {
        watchRounds.current += 1
        void fetchGroups()
      }, WATCH_INTERVAL_MS)
    }
    return () => clearTimeout(timer)
  }, [watchForGroup, groups])

  const deferredSearch = useDeferredValue(!debouncedSearch ? '' : search)

  const [groupsWithExpired, setGroupsWithExpired] = useState<
    Map<string, string>
  >(new Map())
  const handleHasExpiredItems = useCallback(
    (groupId: string, groupName: string, hasExpired: boolean): void => {
      setGroupsWithExpired((prev) => {
        const next = new Map(prev)
        if (hasExpired) {
          next.set(groupId, groupName)
        } else {
          next.delete(groupId)
        }
        return next
      })
    },
    []
  )

  const [groupsWithConflicts, setGroupsWithConflicts] = useState<
    Map<string, string>
  >(new Map())
  const handleHasConflicts = useCallback(
    (groupId: string, groupName: string, hasConflicts: boolean): void => {
      setGroupsWithConflicts((prev) => {
        const next = new Map(prev)
        if (hasConflicts) {
          next.set(groupId, groupName)
        } else {
          next.delete(groupId)
        }
        return next
      })
    },
    []
  )

  const hash = window.location.hash.substring(1)
  const configRealm = config?.realms.find((r) => r.id === hash)
  const me =
    config !== undefined
      ? configRealm
        ? (configRealm.targets[0] ?? '')
        : hash
      : ''
  let filterByMembers: string[] | undefined = undefined

  if (hash && config) {
    if (configRealm) {
      filterByMembers = configRealm.targets
    } else if (hash === 'nobody') {
      filterByMembers = []
    }
  }

  return (
    <>
      {groupsWithConflicts.size > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.75
            }}
          >
            <span>
              {groupsWithConflicts.size === 1
                ? '1 group has'
                : `${groupsWithConflicts.size} groups have`}{' '}
              alias conflicts to review.
            </span>
            {[...groupsWithConflicts.entries()]
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([groupId, groupName]) => (
                <Chip
                  key={groupId}
                  label={groupName}
                  size="small"
                  component="a"
                  href={`#${groupId}`}
                  clickable
                  color="error"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              ))}
          </Box>
        </Alert>
      )}
      {groupsWithExpired.size > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.75
            }}
          >
            <span>
              {groupsWithExpired.size === 1
                ? '1 group has'
                : `${groupsWithExpired.size} groups have`}{' '}
              expired aliases needing review.
            </span>
            {[...groupsWithExpired.entries()]
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([groupId, groupName]) => (
                <Chip
                  key={groupId}
                  label={groupName}
                  size="small"
                  component="a"
                  href={`#${groupId}`}
                  clickable
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              ))}
          </Box>
        </Alert>
      )}
      <div className="groups">
        {ready &&
          groups.map((email) => (
            <Group
              key={email}
              id={email}
              me={me}
              search={deferredSearch}
              clockTick={clockTick}
              filterByMembers={filterByMembers}
              onHasExpiredItems={handleHasExpiredItems}
              onHasConflicts={handleHasConflicts}
            />
          ))}
      </div>
      <Backdrop open={!ready}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  )
}
